import { useEffect, useState } from 'react'
import {
  eliminateOrganizationPosition,
  fetchOrganizationPositions,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { OrganizationPosition } from '../../store/types'

// Temporary CRUD view for org positions in the caller's current org.
// Mirrors HostsSheet/DevicesSheet: list + per-row Eliminate. Rename
// is intentionally omitted — positions don't carry a name field;
// display name comes from the joined JD title and is shared across
// every position using that JD, so a per-position rename would mean
// "swap to a different JD," which is a picker UX out of scope here.
//
// Reachable via gear → About → Debug → "Positions".
export function PositionsSheet() {
  const positions = useAppStore((s) => s.organizationPositions)
  const orgs = useAppStore((s) => s.availableOrganizations)
  const currentOrg = orgs.find((o) => o.is_current)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (currentOrg?.id) fetchOrganizationPositions(currentOrg.id)
  }, [currentOrg?.id])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  function refresh() {
    if (currentOrg?.id) fetchOrganizationPositions(currentOrg.id)
  }

  if (!currentOrg) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 14 }}>
        <p style={{ opacity: 0.6 }}>No current organization.</p>
      </div>
    )
  }

  const live = positions.filter((p) => !p.eliminated_at)
  const temps = live.filter((p) => p.is_temp)
  const permanent = live.filter((p) => !p.is_temp)

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      {temps.length > 0 && (
        <p style={{ opacity: 0.6, marginTop: 0 }}>
          {temps.length} temporary position{temps.length !== 1 ? 's' : ''} not shown.
        </p>
      )}

      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {permanent.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No positions in this organization.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {permanent.map((p) => (
            <PositionRow
              key={p.id}
              position={p}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function PositionRow({
  position,
  onError,
  onClearError,
  onMutated,
}: {
  position: OrganizationPosition
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)

  async function handleEliminate() {
    if (!confirm(`Eliminate position "${position.name}"?`)) return
    onClearError()
    setBusy(true)
    try {
      let resp = await eliminateOrganizationPosition({ id: position.id })
      if (resp?.error === 'has_live_dependents') {
        const agents = resp.blockers?.agents ?? []
        const names = agents.map((a) => `• ${a.name}`).join('\n')
        const ok = confirm(
          `This position has ${agents.length} live agent(s):\n${names}\n\n` +
            `Retire them and eliminate the position?`,
        )
        if (!ok) {
          setBusy(false)
          return
        }
        resp = await eliminateOrganizationPosition({ id: position.id, cascade: true })
      }
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 600 }}>{position.name}</div>
        <div style={{ opacity: 0.6, fontSize: 12 }}>
          {position.parent_position_id ? 'child' : 'top-level'}
        </div>
      </div>
      <div>
        <button
          onClick={handleEliminate}
          disabled={busy}
          style={{ color: '#900' }}
        >
          Eliminate
        </button>
      </div>
    </li>
  )
}
